import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DealContext from '../../providers/Deal';
import classNames from 'classnames';
import InputField from 'components/InputField';
import validations from 'validations';
import { SUCCESS_MESSAGES } from 'request-responses';
import SelectField from 'components/SelectField';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import useCallbackRef from 'hooks/useCallbackRef';
import ToggleField from 'components/ToggleField';
import TextAreaField from 'components/TextAreaField';
import {
  SideDrawerProps,
  SideDrawerRef,
} from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import utils from 'utils';
import { debounce } from 'lodash';
import SearchIcon from 'icons/Search.icon';
import BossModeBadge from 'components/BossModeBadge';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import {
  DEBOUNCE_TIME,
  MAX_PERCENTAGE,
  MAX_PERCENTAGE_TEXT,
} from 'data/global';
import Select, {
  OptionValue,
} from 'ncoded-component-library/build/components/molecules/Select/Select.component';

import './DealEditDrawer.styles.scss';
import './DealEditDrawer.styles.responsive.scss';

const DEAL_NOTES_LIMIT = 5000;
const RECORD_HUBSPOT_ID_MAX = 100;
const RECORD_HUBSPOT_ID_MIN = 2;

type DealEditDrawerProps = {
  className?: string;
} & SideDrawerProps;

const DealEditDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  DealEditDrawerProps
> = (props, ref) => {
  const { className, ...rest } = props;
  const { t } = useTranslation();

  const {
    dealRoom: {
      _id: dealRoomId,
      bossModeReferrerOptions,
      recordHubspotId,
      relationshipManager,
      brokerUpfrontFee,
      resolveShareUpfrontFee,
      brokerMonthlyRetainer,
      resolveShareRetainer,
      brokerSuccessFee,
      resolveShareSuccessFee,
      vendorIntro,
      inLieuListingFee,
      buyerIntroducedByResolve,
      askingPrice,
      leadBroker,
      brokerFirm,
      ACN,
      ABN,
      postcode,
      brokerReferenceCode,
      dealNotes,
    },
    setDealRoom,
  } = useContext(DealContext);

  const defaultEmptySearchOption = useMemo(
    () => [
      {
        label: t('typeToViewAvailableOptions'),
        value: null,
        disabled: true,
      },
    ],
    [t],
  );

  const defaultLeadBrokerOption = useMemo(
    () =>
      leadBroker
        ? [{ value: leadBroker, label: leadBroker }]
        : defaultEmptySearchOption,
    [defaultEmptySearchOption, leadBroker],
  );

  const defaultBrokerFirmOption = useMemo(
    () =>
      brokerFirm
        ? [{ value: brokerFirm, label: brokerFirm }]
        : defaultEmptySearchOption,
    [brokerFirm, defaultEmptySearchOption],
  );

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);
  const [isRequestLoading, setIsRequestLoading] = useState(false);

  //leadBroker and brokerFirm select states
  const [leadBrokerOptions, setLeadBrokerOptions] = useState<
    OptionValue<string>[]
  >(defaultLeadBrokerOption);
  const [brokerFirmOptions, setBrokerFirmOptions] = useState<
    OptionValue<string>[]
  >(defaultBrokerFirmOption);
  const [selectedLeadBroker, setSelectedLeadBroker] = useState(leadBroker);
  const [selectedBrokerFirm, setSelectedBrokerFirm] = useState(brokerFirm);
  const searchLeadBroker = useRef<string>(leadBroker);
  const searchBrokerFirm = useRef<string>(brokerFirm);

  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const initialValues = useMemo(
    () => ({
      recordHubspotId,
      relationshipManager,
      brokerUpfrontFee: utils.getDotedNumber(brokerUpfrontFee),
      resolveShareUpfrontFee: utils.getDotedNumber(resolveShareUpfrontFee),
      brokerMonthlyRetainer: utils.getDotedNumber(brokerMonthlyRetainer),
      resolveShareRetainer: utils.getDotedNumber(resolveShareRetainer),
      brokerSuccessFee: utils.getDotedNumber(brokerSuccessFee),
      resolveShareSuccessFee: utils.getDotedNumber(resolveShareSuccessFee),
      askingPrice: utils.getDotedNumber(askingPrice),
      vendorIntro,
      inLieuListingFee,
      buyerIntroducedByResolve,
      leadBroker,
      brokerFirm,
      ACN,
      ABN,
      postcode,
      brokerReferenceCode,
      dealNotes,
    }),
    [
      recordHubspotId,
      relationshipManager,
      brokerUpfrontFee,
      resolveShareUpfrontFee,
      brokerMonthlyRetainer,
      resolveShareRetainer,
      brokerSuccessFee,
      resolveShareSuccessFee,
      askingPrice,
      vendorIntro,
      inLieuListingFee,
      buyerIntroducedByResolve,
      leadBroker,
      brokerFirm,
      ACN,
      ABN,
      postcode,
      brokerReferenceCode,
      dealNotes,
    ],
  );

  const colleagueOptions = useMemo(
    () =>
      bossModeReferrerOptions?.map((value) => ({
        value,
        label: value,
      })),
    [bossModeReferrerOptions],
  );

  const onSubmitHandle = useCallback(
    async (values) => {
      const {
        relationshipManager,
        recordHubspotId,
        ACN,
        ABN,
        postcode,
        brokerReferenceCode,
        dealNotes,
        ...restValues
      } = values;

      const formValues = restValues;

      try {
        setIsRequestLoading(true);
        const { data } = await api.dealRoom.updateDealRoom(
          dealRoomId,
          {
            ...formValues,
            brokerUpfrontFee: utils.getIntegerFromDottedNumber(
              formValues?.brokerUpfrontFee,
            ),
            resolveShareUpfrontFee: +formValues?.resolveShareUpfrontFee,
            brokerMonthlyRetainer: utils.getIntegerFromDottedNumber(
              formValues?.brokerMonthlyRetainer,
            ),
            resolveShareRetainer: +formValues?.resolveShareRetainer,
            askingPrice: utils.getIntegerFromDottedNumber(
              formValues?.askingPrice,
            ),
            brokerSuccessFee: +formValues?.brokerSuccessFee,
            resolveShareSuccessFee: +formValues?.resolveShareSuccessFee,
            leadBroker: selectedLeadBroker || null,
            brokerFirm: selectedBrokerFirm || null,
            ACN: ACN || null,
            ABN: ABN || null,
            postcode: postcode || null,
            brokerReferenceCode: brokerReferenceCode || null,
            dealNotes: dealNotes || null,
            recordHubspotId: recordHubspotId || null,
            relationshipManager,
          },
          isSuperAdmin,
        );

        setDealRoom((old) => ({ ...old, ...data }));
        popSuccess(t(SUCCESS_MESSAGES.EDIT_DEAL));
        drawer?.setOpen(false);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsRequestLoading(false);
      }
    },
    [
      dealRoomId,
      selectedLeadBroker,
      selectedBrokerFirm,
      isSuperAdmin,
      setDealRoom,
      popSuccess,
      t,
      drawer,
      popServerError,
    ],
  );

  const onLeadBrokerSearch = debounce(async (searchString: string) => {
    try {
      setSelectedLeadBroker(searchString);

      if (searchString && searchLeadBroker.current) {
        const searchOption = { label: searchString, value: searchString };

        const { data } = await api.dealRoom.searchLeadBroker(searchString);

        if (data?.length) {
          const containsSearch = data?.find((el) => el === searchString);

          setLeadBrokerOptions([
            ...data.map((el) => ({ label: el, value: el })),
            ...(containsSearch ? [] : [searchOption]),
          ]);
        } else {
          setLeadBrokerOptions([searchOption]);
        }
      } else {
        setLeadBrokerOptions(defaultEmptySearchOption);
      }
    } catch (e) {
      return;
    }
  }, DEBOUNCE_TIME);

  const onBrokerFirmSearch = debounce(async (searchString: string) => {
    try {
      setSelectedBrokerFirm(searchString);

      if (searchString && searchBrokerFirm.current) {
        const searchOption = { label: searchString, value: searchString };

        const { data } = await api.dealRoom.searchBrokerFirm(searchString);

        if (data?.length) {
          const containsSearch = data?.find((el) => el === searchString);

          setBrokerFirmOptions([
            ...data.map((el) => ({ label: el, value: el })),
            ...(containsSearch ? [] : [searchOption]),
          ]);
        } else {
          setBrokerFirmOptions([searchOption]);
        }
      } else {
        setBrokerFirmOptions(defaultEmptySearchOption);
      }
    } catch (e) {
      return;
    }
  }, DEBOUNCE_TIME);

  const RenderSelectField = useCallback(
    (name: string, label: string, options, required?: boolean) => (
      <Field
        name={name}
        label={label}
        placeholder={label}
        required={required}
        component={SelectField}
        options={options}
        {...(required && {
          validate: validations.required(),
        })}
      />
    ),
    [],
  );

  const RenderToggleFiled = useCallback(
    (name: string, label: string) => (
      <>
        <Field
          name={name}
          label={label}
          type="checkbox"
          className="black-toggle"
          component={ToggleField}
        />
      </>
    ),
    [],
  );

  return (
    <SideDrawer
      {...rest}
      ref={drawerRef}
      className={classNames('deal-edit-drawer', className)}
    >
      <Form
        initialValues={initialValues}
        onSubmit={onSubmitHandle}
        render={({ handleSubmit, invalid }) => (
          <form onSubmit={handleSubmit} className="deal-edit-drawer__form">
            <h1>
              {t('resolveInternalDetails')} <BossModeBadge />
            </h1>
            <p className="deal-edit-drawer__description">
              {t('detailsDisplayedInternally')}
            </p>

            <div className="resolve-commision-container">
              <div className="content-comm">
                <p className="deal-edit-drawer__title">
                  {t('reasonForCommission')}
                </p>

                {RenderToggleFiled('vendorIntro', t('vendorIntro'))}
                {RenderToggleFiled('inLieuListingFee', t('inLieuOf'))}
                {RenderToggleFiled(
                  'buyerIntroducedByResolve',
                  t('conditionalOnTheBuyer'),
                )}
              </div>

              <div className="form-row">
                <Field
                  name="brokerUpfrontFee"
                  label={t('brokerUpfrontFee')}
                  placeholder="$"
                  numberStyle="dottedNumber"
                  component={InputField}
                  validate={validations.validDecimalNumbersOnlyValidation(
                    t('numbersOnlyError'),
                  )}
                />
                <Field
                  name="resolveShareUpfrontFee"
                  label={t('resolvesShareOfFee')}
                  placeholder="%"
                  numberStyle="dottedNumber"
                  component={InputField}
                  validate={validations.composeValidators(
                    validations.greatherThanZero(
                      t('onlyPositiveNumbersAreAllowed'),
                    ),
                    validations.maxNumber(
                      t('maxNumber', {
                        max: MAX_PERCENTAGE_TEXT,
                      }),
                      MAX_PERCENTAGE,
                    ),
                  )}
                />
              </div>
              <div className="form-row">
                <Field
                  name="brokerMonthlyRetainer"
                  label={t('brokerMonthlyRetainer')}
                  placeholder="$"
                  numberStyle="dottedNumber"
                  component={InputField}
                  validate={validations.validDecimalNumbersOnlyValidation(
                    t('numbersOnlyError'),
                  )}
                />
                <Field
                  name="resolveShareRetainer"
                  label={t('resolvesShareOfRetainer')}
                  placeholder="%"
                  numberStyle="dottedNumber"
                  component={InputField}
                  validate={validations.composeValidators(
                    validations.greatherThanZero(
                      t('onlyPositiveNumbersAreAllowed'),
                    ),
                    validations.maxNumber(
                      t('maxNumber', {
                        max: MAX_PERCENTAGE_TEXT,
                      }),
                      MAX_PERCENTAGE,
                    ),
                  )}
                />
              </div>
              <div className="form-row">
                <Field
                  name="askingPrice"
                  label={t('askingPriceInDollar')}
                  placeholder="$"
                  numberStyle="dottedNumber"
                  component={InputField}
                  validate={validations.validDecimalNumbersOnlyValidation(
                    t('numbersOnlyError'),
                  )}
                />
                <Field
                  name="brokerSuccessFee"
                  label={t('brokersSuccessFee')}
                  placeholder="%"
                  numberStyle="dottedNumber"
                  component={InputField}
                  validate={validations.composeValidators(
                    validations.greatherThanZero(
                      t('onlyPositiveNumbersAreAllowed'),
                    ),
                    validations.maxNumber(
                      t('maxNumber', {
                        max: MAX_PERCENTAGE_TEXT,
                      }),
                      MAX_PERCENTAGE,
                    ),
                  )}
                />
              </div>
              <div className="form-row">
                <div className="spacer" />
                <Field
                  name="resolveShareSuccessFee"
                  label={t('resolveShareOfSuccessFee')}
                  placeholder="%"
                  numberStyle="dottedNumber"
                  component={InputField}
                  validate={validations.composeValidators(
                    validations.greatherThanZero(
                      t('onlyPositiveNumbersAreAllowed'),
                    ),
                    validations.maxNumber(
                      t('maxNumber', {
                        max: MAX_PERCENTAGE_TEXT,
                      }),
                      MAX_PERCENTAGE,
                    ),
                  )}
                />
              </div>
            </div>

            <div className="resolve-tracking-container">
              <p className="deal-edit-drawer__title">
                {t('internalTrackingMetrics')}
              </p>
              <p className="deal-edit-drawer__description">
                {t('internalTrackingMetricsDescription')}
              </p>

              <div className="deal-edit-drawer__select">
                <label>{t('brokerLeadManagingThisDeal')}</label>
                <Select
                  options={leadBrokerOptions}
                  value={searchLeadBroker.current}
                  placeholder=""
                  searchable
                  async
                  icon={<SearchIcon />}
                  onSearchChange={(event) => {
                    const searchString = event.target.value;

                    searchLeadBroker.current = searchString;
                    onLeadBrokerSearch(searchString);
                  }}
                  onChange={({ value }: { value: string }) => {
                    searchLeadBroker.current = value;
                    setSelectedLeadBroker(value);
                  }}
                />
              </div>

              <div className="deal-edit-drawer__select">
                <label>{t('brokerFirmManagingThisDeal')}</label>
                <Select
                  options={brokerFirmOptions}
                  value={selectedBrokerFirm}
                  placeholder=""
                  searchable
                  async
                  icon={<SearchIcon />}
                  onSearchChange={(event) => {
                    const searchString = event.target.value;

                    searchBrokerFirm.current = searchString;
                    onBrokerFirmSearch(searchString);
                  }}
                  onChange={({ value }: { value: string }) => {
                    searchBrokerFirm.current = value;
                    setSelectedBrokerFirm(value);
                  }}
                />
              </div>

              <Field
                name="ACN"
                type="number"
                label={t('acnOfListing')}
                component={InputField}
                validate={validations.acnField(t('acnInvalid'))}
              />

              <Field
                name="ABN"
                type="number"
                label={t('abnOfListing')}
                component={InputField}
                validate={validations.abnField(t('abnInvalid'))}
              />

              <Field
                name="postcode"
                type="number"
                label={t('postcodeOfTheListing')}
                component={InputField}
                validate={validations.auPostalCode(t('postalCodeInvalid'))}
              />

              <Field
                name="brokerReferenceCode"
                label={t('brokerReferenceCode')}
                component={InputField}
              />

              <Field
                name="dealNotes"
                label={t('dealNotes')}
                description={t('dealNotesDescriptions')}
                component={TextAreaField}
                validate={validations.noLongerThan(
                  DEAL_NOTES_LIMIT,
                  t('noLongerThan', { length: DEAL_NOTES_LIMIT }),
                )}
              />

              <Field
                name="recordHubspotId"
                label={t('recordIdHubspot')}
                component={InputField}
                validate={validations.composeValidators(
                  validations.noShorterThan(
                    RECORD_HUBSPOT_ID_MIN,
                    t('minLength', { min: RECORD_HUBSPOT_ID_MIN }),
                  ),
                  validations.noLongerThan(
                    RECORD_HUBSPOT_ID_MAX,
                    t('maxLength', { max: RECORD_HUBSPOT_ID_MAX }),
                  ),
                )}
              />

              {RenderSelectField(
                'relationshipManager',
                t('relationshipManager'),
                colleagueOptions,
              )}
            </div>

            <Button type="submit" disabled={invalid}>
              {t('saveChanges')}
            </Button>

            {isRequestLoading && <Loader />}
          </form>
        )}
      />
    </SideDrawer>
  );
};

export default forwardRef(DealEditDrawer);
